<template>
    <div
        ref="progressBar"
        class="AudioProgressbar"
        @click.stop="handleSeek"
        @mousemove="handleHover"
        @mouseenter="handleEnter"
        @mouseleave="handleLeave">
        <span
            v-for="(segment, index) in segments"
            :key="index"
            class="AudioProgressbar__Segment"
            :style="{ left: `${segment}%` }">
        </span>

        <span
            class="AudioProgressbar__Bar"
            :style="{ width: `${progress}%` }">
        </span>

        <span
            class="AudioProgressbar__TimeTracker"
            v-show="this.isHovered"
            :style="tooltipStyle">
            <span class="AudioProgressbar__TimeTrackerText">{{ episodeTitle }}</span>
            <span class="AudioProgressbar__TimeTrackerDuration">{{ hoverTimeFormatted }}</span>
        </span>
    </div>
</template>

<script>
import {
    calculateBarOffsetPercentage,
    formatDynamicDuration,
    calculateSegments,
} from '../utils/index.js';

export default {
    name: 'AudioProgressbar',
    emits: ['seekTo'],
    data() {
        return {
            isHovered: false,
            hoverOffset: 0,
            hoverTime: 0,
        };
    },
    computed: {
        progress() {
            if (!this.duration || !this.current) return 0;

            return this.current / this.duration * 100;
        },
        segments() {
            const timeArray = this.episodes.map(episode => episode.time);

            return calculateSegments(timeArray, this.duration);
        },
        episodeTitle() {
            const activeEpisodeTitle = this.episodes.filter(episode => this.hoverTime > episode.time).pop();

            if (activeEpisodeTitle) {
                return activeEpisodeTitle.text;
            }

            return '';
        },
        hoverTimeFormatted() {
            return formatDynamicDuration(this.hoverTime);
        },
        tooltipStyle() {
            let transform = 'translateX(-50%)';

            if (this.hoverOffset > 90) {
                transform = 'translateX(-100%)';
            }

            if (this.hoverOffset < 10) {
                transform = 'translateX(0%)';
            }

            return {
                transform,
                left: `${this.hoverOffset}%`,
            };
        },
    },
    methods: {
        handleSeek(event) {
            if (this.disabled) return;

            const { progressBar } = this.$refs;

            if (!progressBar) return;

            const percentage = calculateBarOffsetPercentage(event, progressBar);

            if (percentage) {
                // eslint-disable-next-line no-magic-numbers
                const seekTime = Math.abs((percentage / 100) * this.duration);

                this.$emit('seekTo', seekTime);
            }
        },
        handleHover(event) {
            const { progressBar } = this.$refs;

            if (!progressBar) return;

            const percentage = calculateBarOffsetPercentage(event, progressBar);

            if (percentage) {
                // eslint-disable-next-line no-magic-numbers
                const seekTime = Math.abs((percentage / 100) * this.duration);

                this.hoverOffset = percentage;

                this.hoverTime = seekTime;
            }
        },
        handleEnter() {
            this.isHovered = true;
        },
        handleLeave() {
            this.isHovered = false;
        },
    },
    props: {
        current: {
            type: Number,
            default: 0,
        },
        duration: {
            type: Number,
            default: 0,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        episodes: {
            type: Array,
            default: () => [],
        },
    },
};
</script>

<style lang="scss" scoped>
.AudioProgressbar {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    height: 12px;
    background-color: #ebedf1;
    cursor: pointer;
}

.AudioProgressbar__Bar {
    position: absolute;
    left: 0;
    height: 100%;
    background-color: $primary;
    cursor: pointer;
}

.AudioProgressbar__Segment {
    position: absolute;
    z-index: 2;
    width: 2px;
    height: 100%;
    background-color: $white;
}

.AudioProgressbar__TimeTracker {
    position: absolute;
    bottom: calc(100% + 10px);
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: max-content;
    max-width: 325px;
    padding: 10px 12px;
    color: $white;
    text-align: center;
    background-color: $black;
    border-radius: 10px;
    transform: translateX(-50%);
    opacity: 0.9;

    @include typo-additional-book;
}

.AudioProgressbar__TimeTrackerText {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}
</style>
